import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { toast } from "react-toastify";
import API_BASE_URL from "../../config/api";
import { isRaportCzasowUser } from "../../utils/raportCzasowAccess";
import {
  NAZWY_POL,
  ROLE,
  SCENARIUSZE,
  STATUSY,
  STATUSY_AGENCJI,
  WSZYSCY,
  WSZYSTKIE,
  filtruj,
  formatCzas,
  formatData,
  kolorStatusu,
  kwantyl,
  medianyStatusow,
  podsumuj,
  statystykiOsob,
  zbudujOdprawy,
} from "./raportCzasowUtils";
import { zapiszRaportPdf } from "./eksportPdf";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Tooltip,
  Legend,
);

const pad = (n) => String(n).padStart(2, "0");
const dataLokalna = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dniTemu = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return dataLokalna(d);
};

const LIMIT_PRZEBIEGU = 50;
const LADNE_PODZIALKI = [1, 5, 15, 30, 60, 120, 240, 480, 1440, 2880, 10080, 20160, 43200];

const KOLUMNY = [
  { pole: "nrRef", etykieta: "Ref" },
  { pole: "importer", etykieta: "Importer" },
  { pole: "rodzaj", etykieta: "Rodzaj" },
  { pole: "osobaPrzygotowujacaOdprawe", etykieta: "Przygotował" },
  { pole: "osobaWysylajacaOdprawe", etykieta: "Wysłał" },
  { pole: "utworzono", etykieta: "Utworzono", format: formatData },
  { pole: "scenariusz", etykieta: "Przyjazd towaru", format: (v) => SCENARIUSZE[v]?.krotko || "—" },
  { pole: "minCzekanieNaPrzyjazd", etykieta: "Czekanie na przyjazd", liczba: true },
  { pole: "minAgent", etykieta: "Praca agenta", liczba: true },
  { pole: "minPrzygotowanie", etykieta: "Od utworzenia do wysłania", liczba: true },
  { pole: "minUrzad", etykieta: "Urząd", liczba: true },
  { pole: "minCalosc", etykieta: "Całość", liczba: true },
];

const karta = {
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
  border: "1px solid #E1E5EA",
  borderRadius: 2,
};

const podpis = { color: "#5F6B7A", fontSize: 13, lineHeight: 1.45 };

function Kafel({ etykieta, wartosc, zespol, pokazZespol }) {
  return (
    <Paper elevation={0} sx={karta}>
      <Typography sx={{ ...podpis, fontSize: 12 }}>{etykieta}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
        {wartosc}
      </Typography>
      <Typography sx={{ ...podpis, fontSize: 12, minHeight: 18 }}>
        {pokazZespol ? `Zespół: ${zespol}` : ""}
      </Typography>
    </Paper>
  );
}

const RaportCzasow = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  let username = null;
  try {
    username = token ? jwtDecode(token).username : null;
  } catch (e) {
    username = null;
  }
  const maDostep = isRaportCzasowUser(username);

  const [od, setOd] = useState(dniTemu(30));
  const [doDaty, setDoDaty] = useState(dniTemu(0));
  const [dane, setDane] = useState({ odprawy: [], zmiany: [], obcieto: false });
  const [ladowanie, setLadowanie] = useState(false);
  const [blad, setBlad] = useState(null);

  const [rola, setRola] = useState("osobaPrzygotowujacaOdprawe");
  const [uzytkownik, setUzytkownik] = useState(WSZYSCY);
  const [rodzaj, setRodzaj] = useState(WSZYSTKIE);
  const [wybranaId, setWybranaId] = useState(null);
  const [trybPrzebiegu, setTrybPrzebiegu] = useState("najdluzsze");
  const [scenariusz, setScenariusz] = useState(WSZYSTKIE);
  const [miaraOsob, setMiaraOsob] = useState("minAgent");
  const [szukaj, setSzukaj] = useState("");
  const [sort, setSort] = useState({ pole: "utworzono", kierunek: "desc" });
  const [strona, setStrona] = useState(0);
  const [naStronie, setNaStronie] = useState(25);
  const [trybPdf, setTrybPdf] = useState(false);

  const refNaglowekPdf = useRef(null);
  const refKafle = useRef(null);
  const refWykresy = useRef(null);
  const refPrzebieg = useRef(null);
  const refTabela = useRef(null);
  const refSzczegoly = useRef(null);

  const pobierz = useCallback(async () => {
    if (!maDostep || !od || !doDaty) return;
    setLadowanie(true);
    setBlad(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/raport-czasow-odpraw`, {
        params: { od, do: doDaty },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          "X-Use-New-Db": localStorage.getItem("useNewDb") || "false",
        },
      });
      setDane({
        odprawy: response.data?.odprawy || [],
        zmiany: response.data?.zmiany || [],
        obcieto: Boolean(response.data?.obcieto),
      });
      setWybranaId(null);
      setStrona(0);
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setBlad(`Nie udało się pobrać danych raportu: ${msg}`);
    } finally {
      setLadowanie(false);
    }
  }, [maDostep, od, doDaty]);

  useEffect(() => {
    pobierz();
  }, [pobierz]);

  const odprawy = useMemo(
    () => zbudujOdprawy(dane.odprawy, dane.zmiany),
    [dane],
  );
  const rodzaje = useMemo(
    () => [...new Set(odprawy.map((o) => o.rodzaj).filter(Boolean))].sort(),
    [odprawy],
  );
  const uzytkownicy = useMemo(
    () =>
      [...new Set(odprawy.map((o) => o[rola]).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pl"),
      ),
    [odprawy, rola],
  );

  useEffect(() => {
    if (uzytkownik !== WSZYSCY && !uzytkownicy.includes(uzytkownik)) {
      setUzytkownik(WSZYSCY);
    }
  }, [uzytkownicy, uzytkownik]);

  const jestOsoba = uzytkownik !== WSZYSCY;
  const zespol = useMemo(
    () => filtruj(odprawy, { uzytkownik: WSZYSCY, rola, rodzaj, scenariusz }),
    [odprawy, rola, rodzaj, scenariusz],
  );
  const wybrane = useMemo(
    () => filtruj(odprawy, { uzytkownik, rola, rodzaj, scenariusz }),
    [odprawy, uzytkownik, rola, rodzaj, scenariusz],
  );
  const pods = useMemo(() => podsumuj(wybrane), [wybrane]);
  const podsZespol = useMemo(() => podsumuj(zespol), [zespol]);
  const stWybrane = useMemo(() => medianyStatusow(wybrane), [wybrane]);
  const stZespol = useMemo(() => medianyStatusow(zespol), [zespol]);
  const osoby = useMemo(
    () => statystykiOsob(zespol, rola, miaraOsob),
    [zespol, rola, miaraOsob],
  );

  const najdluzszyStatus = useMemo(() => {
    const kandydaci = STATUSY_AGENCJI.filter((s) => stWybrane[s]);
    if (!kandydaci.length) return null;
    return kandydaci.reduce((a, b) =>
      stWybrane[b].mediana > stWybrane[a].mediana ? b : a,
    );
  }, [stWybrane]);

  const opisWyboru = `${jestOsoba ? uzytkownik : "Cały zespół"}${
    rodzaj !== WSZYSTKIE ? ` · ${rodzaj}` : ""
  }${scenariusz !== WSZYSTKIE ? ` · ${SCENARIUSZE[scenariusz]?.nazwa}` : ""}`;

  /* ---------- wykres: gdzie ucieka czas ---------- */
  const statusyWykresu = useMemo(() => {
    const inne = Object.keys(stZespol).filter((s) => !STATUSY.includes(s));
    return [...STATUSY, ...inne].filter((s) => stWybrane[s] || (jestOsoba && stZespol[s]));
  }, [stWybrane, stZespol, jestOsoba]);

  const daneStatusow = {
    labels: statusyWykresu,
    datasets: [
      {
        label: jestOsoba ? uzytkownik : "Zespół",
        data: statusyWykresu.map((s) => stWybrane[s]?.mediana ?? null),
        backgroundColor: statusyWykresu.map(kolorStatusu),
        borderRadius: 4,
        maxBarThickness: 18,
      },
      ...(jestOsoba
        ? [
            {
              label: "Zespół",
              data: statusyWykresu.map((s) => stZespol[s]?.mediana ?? null),
              backgroundColor: statusyWykresu.map((s) => `${kolorStatusu(s)}55`),
              borderRadius: 4,
              maxBarThickness: 18,
            },
          ]
        : []),
    ],
  };
  const opcjeStatusow = {
    indexAxis: "y",
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: jestOsoba, position: "bottom" },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatCzas(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { callback: (v) => formatCzas(v) } },
      y: { grid: { display: false } },
    },
  };

  /* ---------- wykres: czas przygotowania wg osoby ---------- */
  const daneOsob = {
    labels: osoby.map((o) => `${o.uzytkownik} (${o.liczbaZMiara})`),
    datasets: [
      {
        label: miaraOsob === "minAgent" ? "Mediana pracy agenta" : "Mediana od utworzenia do Wysłane",
        data: osoby.map((o) => Math.max(1, o.wartosc)),
        backgroundColor: osoby.map((o) =>
          !jestOsoba || o.uzytkownik === uzytkownik ? "#3B6FB6" : "#BFCDE3",
        ),
        borderRadius: 4,
        maxBarThickness: 18,
      },
    ],
  };
  const opcjeOsob = {
    indexAxis: "y",
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    onClick: (evt, elementy) => {
      if (!elementy.length) return;
      const u = osoby[elementy[0].index]?.uzytkownik;
      if (u) setUzytkownik(u === uzytkownik ? WSZYSCY : u);
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `Mediana: ${formatCzas(osoby[ctx.dataIndex]?.wartosc)} · odpraw: ${
              osoby[ctx.dataIndex]?.liczbaZMiara
            }`,
        },
      },
    },
    scales: {
      x: {
        type: "logarithmic",
        min: 1,
        afterBuildTicks: (axis) => {
          axis.ticks = LADNE_PODZIALKI.filter((v) => v >= axis.min && v <= axis.max).map(
            (value) => ({ value }),
          );
        },
        ticks: { callback: (v) => formatCzas(v) },
      },
      y: { grid: { display: false } },
    },
  };

  /* ---------- wykres: przebieg odpraw ---------- */
  const przebieg = useMemo(() => {
    const lista = wybrane.filter((o) => o.pobytyAgenta.length > 0);
    lista.sort((a, b) =>
      trybPrzebiegu === "najdluzsze"
        ? b.minSumaAgenta - a.minSumaAgenta
        : String(b.utworzono).localeCompare(String(a.utworzono)),
    );
    return lista.slice(0, LIMIT_PRZEBIEGU);
  }, [wybrane, trybPrzebiegu]);

  const skalaPrzebiegu = useMemo(() => {
    const sumy = przebieg.map((o) => o.minSumaAgenta);
    if (!sumy.length) return 60;
    const max = Math.max(...sumy);
    const cap = Math.max(60, Math.ceil(((kwantyl(sumy, 0.8) || 60) * 1.4) / 30) * 30);
    return Math.min(max, cap);
  }, [przebieg]);

  const statusyPrzebiegu = useMemo(() => {
    const obecne = new Set();
    przebieg.forEach((o) => Object.keys(o.czasWStatusachAgenta).forEach((s) => obecne.add(s)));
    return [...STATUSY.filter((s) => obecne.has(s)), ...[...obecne].filter((s) => !STATUSY.includes(s))];
  }, [przebieg]);

  const danePrzebiegu = {
    labels: przebieg.map(
      (o) => `${o.nrRef || o.id}${o.scenariusz === "czekala" ? " ⏸" : ""}`,
    ),
    datasets: statusyPrzebiegu.map((s) => ({
      label: s,
      data: przebieg.map((o) => o.czasWStatusachAgenta[s] || 0),
      backgroundColor: kolorStatusu(s),
      stack: "czas",
      barThickness: 14,
    })),
  };
  const opcjePrzebiegu = {
    indexAxis: "y",
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    onClick: (evt, elementy) => {
      if (!elementy.length) return;
      const o = przebieg[elementy[0].index];
      if (o) setWybranaId(o.id === wybranaId ? null : o.id);
    },
    plugins: {
      legend: { position: "top", labels: { boxWidth: 12 } },
      tooltip: {
        filter: (item) => item.raw > 0,
        callbacks: {
          title: (items) => {
            const o = przebieg[items[0]?.dataIndex];
            return o ? `Ref ${o.nrRef || o.id} · ${o.importer || ""}` : "";
          },
          label: (ctx) => `${ctx.dataset.label}: ${formatCzas(ctx.raw)}`,
          footer: (items) => {
            const o = przebieg[items[0]?.dataIndex];
            if (!o) return "";
            const linie = [
              `Praca agenta od ${o.startAgentaOd}: ${formatCzas(o.minAgent)}`,
              `Razem od startu: ${formatCzas(o.minSumaAgenta)}`,
            ];
            if (o.minCzekanieNaPrzyjazd != null) {
              linie.push(`Czekanie na przyjazd: ${formatCzas(o.minCzekanieNaPrzyjazd)}`);
            }
            return linie;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: skalaPrzebiegu,
        ticks: { callback: (v) => formatCzas(v) },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: (ctx) => ({
            weight: przebieg[ctx.index]?.id === wybranaId ? "bold" : "normal",
          }),
        },
      },
    },
  };

  /* ---------- tabela ---------- */
  const wierszeTabeli = useMemo(() => {
    const q = szukaj.trim().toLowerCase();
    let lista = q
      ? wybrane.filter((o) =>
          [o.id, o.nrRef, o.importer, o.osobaPrzygotowujacaOdprawe, o.osobaWysylajacaOdprawe, o.rodzaj]
            .join(" ")
            .toLowerCase()
            .includes(q),
        )
      : [...wybrane];
    const kier = sort.kierunek === "asc" ? 1 : -1;
    lista.sort((a, b) => {
      const va = a[sort.pole];
      const vb = b[sort.pole];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return (typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "pl")) * kier;
    });
    return lista;
  }, [wybrane, szukaj, sort]);

  const widoczneWiersze = trybPdf
    ? wierszeTabeli
    : wierszeTabeli.slice(strona * naStronie, strona * naStronie + naStronie);

  const zmienSort = (pole) =>
    setSort((s) =>
      s.pole === pole
        ? { pole, kierunek: s.kierunek === "asc" ? "desc" : "asc" }
        : { pole, kierunek: pole.startsWith("min") ? "desc" : "asc" },
    );

  const wybrana = useMemo(
    () => odprawy.find((o) => o.id === wybranaId) || null,
    [odprawy, wybranaId],
  );

  const wybierzOdprawe = (id) => {
    setWybranaId((obecna) => (obecna === id ? null : id));
    setTimeout(() => refSzczegoly.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };

  /* ---------- PDF ---------- */
  useEffect(() => {
    if (!trybPdf) return undefined;
    const timer = setTimeout(async () => {
      try {
        await zapiszRaportPdf(
          [
            refNaglowekPdf.current,
            refKafle.current,
            refWykresy.current,
            refPrzebieg.current,
            wybrana ? refSzczegoly.current : null,
            refTabela.current,
          ],
          `raport-czasow-odpraw_${od}_${doDaty}.pdf`,
        );
      } catch (error) {
        console.error("Błąd zapisu PDF:", error);
        toast.error("Nie udało się zapisać PDF. Spróbuj ponownie.");
      } finally {
        setTrybPdf(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [trybPdf]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!maDostep) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper elevation={0} sx={{ ...karta, maxWidth: 520 }}>
          <Typography variant="h6">Raport czasów odpraw</Typography>
          <Typography sx={podpis}>Nie masz dostępu do tego modułu.</Typography>
          <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/user")}>
              Wróć do listy odpraw
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const wygenerowano = new Date();
  const historiaWybranej = wybrana
    ? [...wybrana.historia].sort((a, b) => String(a.editedAt).localeCompare(String(b.editedAt)))
    : [];

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Pasek górny */}
        <Box
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, justifyContent: "space-between" }}
          data-html2canvas-ignore="true"
        >
          <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
            Raport czasów odpraw
          </Typography>
          <Button
            variant="contained"
            startIcon={trybPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
            onClick={() => setTrybPdf(true)}
            disabled={trybPdf || ladowanie || !odprawy.length}
          >
            {trybPdf ? "Zapisuję PDF…" : "Zapisz PDF"}
          </Button>
        </Box>

        {/* Nagłówek widoczny tylko w PDF */}
        {trybPdf && (
          <Box ref={refNaglowekPdf} sx={{ background: "#fff", p: 2, display: "flex", gap: 2, alignItems: "center", borderBottom: "2px solid #3B6FB6" }}>
            <img src="/logo-wega.jpg" alt="" style={{ height: 44 }} />
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 500 }}>Raport czasów odpraw</Typography>
              <Typography sx={podpis}>
                Odprawy utworzone {formatData(`${od} 00:00`).slice(0, 10)} – {formatData(`${doDaty} 00:00`).slice(0, 10)} ·{" "}
                {opisWyboru} · przypisanie do {ROLE[rola]} · odpraw: {pods.liczba}
              </Typography>
              <Typography sx={{ ...podpis, fontSize: 12 }}>
                Wygenerowano {formatData(`${dataLokalna(wygenerowano)} ${pad(wygenerowano.getHours())}:${pad(wygenerowano.getMinutes())}`)} przez {username}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Filtry */}
        <Paper elevation={0} sx={{ ...karta, flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 2 }} data-html2canvas-ignore="true">
          <TextField
            id="raport-od"
            label="Utworzone od"
            type="date"
            size="small"
            value={od}
            onChange={(e) => e.target.value && setOd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            id="raport-do"
            label="Utworzone do"
            type="date"
            size="small"
            value={doDaty}
            onChange={(e) => e.target.value && setDoDaty(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="raport-uzytkownik-l">Użytkownik</InputLabel>
            <Select
              labelId="raport-uzytkownik-l"
              id="raport-uzytkownik"
              label="Użytkownik"
              value={uzytkownicy.includes(uzytkownik) ? uzytkownik : WSZYSCY}
              onChange={(e) => setUzytkownik(e.target.value)}
            >
              <MenuItem value={WSZYSCY}>Wszyscy (zespół)</MenuItem>
              {uzytkownicy.map((u) => (
                <MenuItem key={u} value={u}>
                  {u}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ ...podpis, fontSize: 12 }}>Przypisz odprawę do</Typography>
            <ButtonGroup size="small">
              {Object.entries(ROLE).map(([pole, nazwa]) => (
                <Button
                  key={pole}
                  variant={rola === pole ? "contained" : "outlined"}
                  onClick={() => setRola(pole)}
                >
                  {nazwa}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="raport-rodzaj-l">Rodzaj odprawy</InputLabel>
            <Select
              labelId="raport-rodzaj-l"
              id="raport-rodzaj"
              label="Rodzaj odprawy"
              value={rodzaj === WSZYSTKIE || rodzaje.includes(rodzaj) ? rodzaj : WSZYSTKIE}
              onChange={(e) => setRodzaj(e.target.value)}
            >
              <MenuItem value={WSZYSTKIE}>Wszystkie</MenuItem>
              {rodzaje.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 230 }}>
            <InputLabel id="raport-scenariusz-l">Przyjazd towaru</InputLabel>
            <Select
              labelId="raport-scenariusz-l"
              id="raport-scenariusz"
              label="Przyjazd towaru"
              value={scenariusz}
              onChange={(e) => setScenariusz(e.target.value)}
            >
              <MenuItem value={WSZYSTKIE}>Wszystkie</MenuItem>
              {Object.entries(SCENARIUSZE).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v.nazwa}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            onClick={() => {
              setUzytkownik(WSZYSCY);
              setRodzaj(WSZYSTKIE);
              setScenariusz(WSZYSTKIE);
              setRola("osobaPrzygotowujacaOdprawe");
              setWybranaId(null);
            }}
          >
            Wyczyść filtry
          </Button>
          {ladowanie && <CircularProgress size={20} />}
        </Paper>

        {blad && <Alert severity="error">{blad}</Alert>}
        {dane.obcieto && (
          <Alert severity="warning">
            W tym zakresie jest więcej niż 5000 odpraw. Raport pokazuje 5000 najnowszych; zawęź zakres dat, aby zobaczyć wszystkie.
          </Alert>
        )}
        {!ladowanie && !blad && !odprawy.length && (
          <Alert severity="info">Brak odpraw utworzonych w wybranym zakresie dat.</Alert>
        )}

        {odprawy.length > 0 && (
          <>
            {/* Kafle */}
            <Box ref={refKafle} sx={{ display: "flex", flexDirection: "column", gap: 1.5, background: "#F4F6F8" }}>
              <Typography sx={{ fontSize: 15, color: "#2A3340" }}>
                <b>{opisWyboru}</b>: mediana czasu pracy agenta (od przyjazdu towaru albo od utworzenia, gdy towar był na miejscu, do „Wysłane”) to <b>{formatCzas(pods.agent)}</b>, a odprawa w urzędzie trwa <b>{formatCzas(pods.urzad)}</b>.
                {pods.czekalyLiczba > 0 && (
                  <>
                    {" "}Na przyjazd towaru czekało {pods.czekalyLiczba} odpraw (mediana czekania <b>{formatCzas(pods.czekanie)}</b>), z czego {pods.przygotowanePrzed} było przygotowanych przed przyjazdem.
                  </>
                )}
                {najdluzszyStatus && (
                  <>
                    {" "}Przed wysłaniem zgłoszenia najdłużej stoją w statusie <b>„{najdluzszyStatus}”</b>.
                  </>
                )}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.5 }}>
                <Kafel etykieta="Liczba odpraw" wartosc={pods.liczba} zespol={podsZespol.liczba} pokazZespol={jestOsoba} />
                <Kafel etykieta="Praca agenta · przyjazd lub utworzenie → Wysłane" wartosc={formatCzas(pods.agent)} zespol={formatCzas(podsZespol.agent)} pokazZespol={jestOsoba} />
                <Kafel etykieta="Czekanie na przyjazd · utworzenie → przyjazd" wartosc={formatCzas(pods.czekanie)} zespol={formatCzas(podsZespol.czekanie)} pokazZespol={jestOsoba} />
                <Kafel etykieta="Od utworzenia · utworzenie → Wysłane" wartosc={formatCzas(pods.przygotowanie)} zespol={formatCzas(podsZespol.przygotowanie)} pokazZespol={jestOsoba} />
                <Kafel etykieta="Urząd · Wysłane → Odprawa zakończona" wartosc={formatCzas(pods.urzad)} zespol={formatCzas(podsZespol.urzad)} pokazZespol={jestOsoba} />
                <Kafel etykieta="Cały proces · utworzenie → Zrealizowane" wartosc={formatCzas(pods.calosc)} zespol={formatCzas(podsZespol.calosc)} pokazZespol={jestOsoba} />
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Typography sx={{ ...podpis, fontSize: 12 }}>Przyjazd towaru:</Typography>
                {Object.entries(SCENARIUSZE).map(([k, v]) => (
                  <MuiTooltip key={k} title={v.opis}>
                    <Chip
                      size="small"
                      label={`${v.nazwa}: ${pods.scenariusze[k] || 0}`}
                      color={scenariusz === k ? "primary" : "default"}
                      variant={scenariusz === k ? "filled" : "outlined"}
                      onClick={() => setScenariusz(scenariusz === k ? WSZYSTKIE : k)}
                    />
                  </MuiTooltip>
                ))}
                {pods.czekalyLiczba > 0 && (
                  <Typography sx={{ ...podpis, fontSize: 12 }}>
                    · przygotowane przed przyjazdem: {pods.przygotowanePrzed} z {pods.czekalyLiczba}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ ...podpis, fontSize: 12 }}>
                Przyjazd towaru to pierwsza zmiana rodzaju transportu z „W drodze”/„Kurier” na „Na Kołach”, „Kurier MU…” lub „Magazyn Warszawa” albo lokalizacji z „W DRODZE”/„NIEZNANA” na konkretne miejsce — która była wcześniej. Odprawa utworzona od razu jako „Na Kołach” (lub „Kurier MU…”, „Magazyn Warszawa”) liczy się od utworzenia. Czasy to mediany w czasie kalendarzowym (z nocami i weekendami).
              </Typography>
            </Box>

            {/* Dwa wykresy porównawcze */}
            <Box ref={refWykresy} sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 1.5, background: "#F4F6F8" }}>
              <Paper elevation={0} sx={karta}>
                <Typography sx={{ fontWeight: 500 }}>Gdzie ucieka czas</Typography>
                <Typography sx={podpis}>
                  Mediana czasu w każdym statusie, liczona od startu pracy agenta (czekanie na przyjazd się nie wlicza).{" "}
                  {jestOsoba ? `Pełny kolor: ${uzytkownik}, jasny: cały zespół.` : "Wybierz osobę, aby porównać ją z zespołem."}
                </Typography>
                <Box sx={{ height: statusyWykresu.length * (jestOsoba ? 44 : 32) + 70 }}>
                  <Bar data={daneStatusow} options={opcjeStatusow} />
                </Box>
              </Paper>
              <Paper elevation={0} sx={karta}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 500 }}>Czas wg osoby</Typography>
                  <ButtonGroup size="small" data-html2canvas-ignore="true">
                    <Button variant={miaraOsob === "minAgent" ? "contained" : "outlined"} onClick={() => setMiaraOsob("minAgent")}>
                      Praca agenta
                    </Button>
                    <Button variant={miaraOsob === "minPrzygotowanie" ? "contained" : "outlined"} onClick={() => setMiaraOsob("minPrzygotowanie")}>
                      Od utworzenia
                    </Button>
                  </ButtonGroup>
                </Box>
                <Typography sx={podpis}>
                  {miaraOsob === "minAgent"
                    ? "Mediana pracy agenta (od przyjazdu towaru lub utworzenia do „Wysłane”)"
                    : "Mediana od utworzenia do „Wysłane” (z czekaniem na przyjazd)"}
                  , w nawiasie liczba odpraw. Skala logarytmiczna. Kliknij słupek, aby wybrać osobę.
                </Typography>
                <Box sx={{ height: Math.max(160, osoby.length * 28 + 50) }}>
                  <Bar data={daneOsob} options={opcjeOsob} />
                </Box>
              </Paper>
            </Box>

            {/* Przebieg */}
            <Paper ref={refPrzebieg} elevation={0} sx={karta}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 500 }}>Przebieg odpraw</Typography>
                <ButtonGroup size="small" data-html2canvas-ignore="true">
                  <Button variant={trybPrzebiegu === "najdluzsze" ? "contained" : "outlined"} onClick={() => setTrybPrzebiegu("najdluzsze")}>
                    {LIMIT_PRZEBIEGU} najdłuższych
                  </Button>
                  <Button variant={trybPrzebiegu === "najnowsze" ? "contained" : "outlined"} onClick={() => setTrybPrzebiegu("najnowsze")}>
                    {LIMIT_PRZEBIEGU} najnowszych
                  </Button>
                </ButtonGroup>
              </Box>
              <Typography sx={podpis}>
                {trybPrzebiegu === "najdluzsze" ? "Najdłuższe" : "Najnowsze"} odprawy (do {LIMIT_PRZEBIEGU}): ile czasu każda spędziła w poszczególnych statusach, licząc od startu pracy agenta. Znak ⏸ przy numerze oznacza odprawę, która czekała na przyjazd towaru; czas czekania jest w podpowiedzi. Skala kończy się na {formatCzas(skalaPrzebiegu)}, dłuższe paski są ucięte (pełny czas w podpowiedzi). Kliknij pasek, aby zobaczyć historię odprawy.
              </Typography>
              {przebieg.length ? (
                <Box sx={{ height: przebieg.length * 22 + 80 }}>
                  <Bar data={danePrzebiegu} options={opcjePrzebiegu} />
                </Box>
              ) : (
                <Typography sx={podpis}>Brak odpraw z historią statusów dla tych filtrów.</Typography>
              )}
            </Paper>

            {/* Historia wybranej odprawy */}
            <Paper ref={refSzczegoly} elevation={0} sx={karta}>
              <Typography sx={{ fontWeight: 500 }}>Historia odprawy</Typography>
              {!wybrana ? (
                <Typography sx={podpis}>
                  Kliknij pasek na wykresie przebiegu albo wiersz w tabeli, aby zobaczyć kolejne zmiany statusu, osoby, lokalizacji i transportu.
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px 20px", ...podpis }}>
                    <span>Ref: <b>{wybrana.nrRef || "—"}</b></span>
                    <span>Importer: <b>{wybrana.importer || "—"}</b></span>
                    <span>Rodzaj: <b>{wybrana.rodzaj || "—"}</b></span>
                    <span>Przygotował: <b>{wybrana.osobaPrzygotowujacaOdprawe || "—"}</b></span>
                    <span>Wysłał: <b>{wybrana.osobaWysylajacaOdprawe || "—"}</b></span>
                    <span>Przyjazd towaru: <b>{SCENARIUSZE[wybrana.scenariusz]?.nazwa}{wybrana.przyjazd ? ` · ${formatData(wybrana.przyjazd.kiedy)} (${wybrana.przyjazd.zrodlo})` : ""}</b></span>
                    <span>Czekanie na przyjazd: <b>{formatCzas(wybrana.minCzekanieNaPrzyjazd)}</b></span>
                    <span>Praca agenta: <b>{formatCzas(wybrana.minAgent)}</b></span>
                    <span>Od utworzenia do wysłania: <b>{formatCzas(wybrana.minPrzygotowanie)}</b></span>
                    <span>Całość: <b>{formatCzas(wybrana.minCalosc)}</b></span>
                  </Box>
                  <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
                    <Box component="li" sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 1.5, py: 0.75 }}>
                      <Typography sx={{ ...podpis, fontVariantNumeric: "tabular-nums" }}>{formatData(wybrana.utworzono)}</Typography>
                      <Typography sx={{ fontSize: 14 }}>Utworzono zgłoszenie</Typography>
                    </Box>
                    {historiaWybranej.map((h, i) => {
                      const pobyt =
                        h.fieldName === "statusRealizacji"
                          ? wybrana.pobyty.find((p) => p.do === h.editedAt && p.nastepny === h.newValue)
                          : null;
                      return (
                        <Box
                          component="li"
                          key={`${h.fieldName}-${h.editedAt}-${i}`}
                          sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 1.5, py: 0.75, borderTop: "1px solid #E9ECF0" }}
                        >
                          <Typography sx={{ ...podpis, fontVariantNumeric: "tabular-nums" }}>{formatData(h.editedAt)}</Typography>
                          <Box>
                            <Chip size="small" variant="outlined" label={NAZWY_POL[h.fieldName] || h.fieldName} sx={{ mr: 1, height: 20, fontSize: 11 }} />
                            {h.fieldName === "statusRealizacji" && (
                              <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: kolorStatusu(h.newValue), mr: 0.75 }} />
                            )}
                            <Typography component="span" sx={{ fontSize: 14 }}>
                              {h.oldValue || "—"} → {h.newValue || "—"} · {h.editedBy || "—"}
                            </Typography>
                            {pobyt && (
                              <Typography sx={{ ...podpis, fontSize: 12 }}>
                                po {formatCzas(pobyt.minuty)} w „{pobyt.status}”
                              </Typography>
                            )}
                            {wybrana.przyjazd &&
                              h.editedAt === wybrana.przyjazd.kiedy &&
                              NAZWY_POL[h.fieldName] === wybrana.przyjazd.zrodlo && (
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#2E9C8F" }}>
                                  {wybrana.scenariusz === "czekala"
                                    ? "▶ Przyjazd towaru — start pracy agenta"
                                    : "Przyjazd towaru (po wysłaniu zgłoszenia)"}
                                </Typography>
                              )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                  {!historiaWybranej.length && (
                    <Typography sx={podpis}>Ta odprawa nie ma zapisanej historii zmian.</Typography>
                  )}
                </>
              )}
            </Paper>

            {/* Tabela */}
            <Paper ref={refTabela} elevation={0} sx={karta}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 500 }}>Lista odpraw ({wierszeTabeli.length})</Typography>
                <TextField
                  id="raport-szukaj"
                  size="small"
                  placeholder="Szukaj: Ref, importer, osoba…"
                  value={szukaj}
                  onChange={(e) => {
                    setSzukaj(e.target.value);
                    setStrona(0);
                  }}
                  data-html2canvas-ignore="true"
                />
              </Box>
              <TableContainer sx={{ maxHeight: trybPdf ? "none" : 640 }}>
                <Table size="small" stickyHeader={!trybPdf}>
                  <TableHead>
                    <TableRow>
                      {KOLUMNY.map((k) => (
                        <TableCell key={k.pole} align={k.liczba ? "right" : "left"} sortDirection={sort.pole === k.pole ? sort.kierunek : false} sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                          <TableSortLabel active={sort.pole === k.pole} direction={sort.pole === k.pole ? sort.kierunek : "asc"} onClick={() => zmienSort(k.pole)}>
                            {k.etykieta}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {widoczneWiersze.map((o) => (
                      <TableRow
                        key={o.id}
                        hover
                        selected={o.id === wybranaId}
                        onClick={() => wybierzOdprawe(o.id)}
                        sx={{ cursor: "pointer" }}
                        data-pdf-break="true"
                      >
                        {KOLUMNY.map((k) => (
                          <TableCell key={k.pole} align={k.liczba ? "right" : "left"} sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {k.liczba ? formatCzas(o[k.pole]) : k.format ? k.format(o[k.pole]) : o[k.pole] ?? "—"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!trybPdf && (
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[25, 50, 100]}
                  count={wierszeTabeli.length}
                  page={Math.min(strona, Math.max(0, Math.ceil(wierszeTabeli.length / naStronie) - 1))}
                  rowsPerPage={naStronie}
                  onPageChange={(e, p) => setStrona(p)}
                  onRowsPerPageChange={(e) => {
                    setNaStronie(parseInt(e.target.value, 10));
                    setStrona(0);
                  }}
                  labelRowsPerPage="Wierszy na stronie"
                />
              )}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default RaportCzasow;
